import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { SignupFormSchema } from '../../schema';

import { Colors, Logo } from '@/assets';
import { Alert, InputText, Loading, PrimaryButton } from '@/components';
import { AuthThunks, LoginGoogleButton, SignupForm, useAuth } from '@/features/auth';
import { classValidatorFormResolverFactory } from '@/helper';
import { useMessageData } from '@/hooks';
import { Screens } from '@/routes';

const SignupFormSchemaValidatorResolver = classValidatorFormResolverFactory<SignupFormSchema>(SignupFormSchema);

export const Signup = () => {
    const { isSuccess, message, setMessage, clearMessage } = useMessageData();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const { invitationToken } = useAuth();

    const form = useForm<SignupFormSchema>({
        resolver: SignupFormSchemaValidatorResolver
    });

    const onSubmit = async (data: SignupForm) => {
        try {
            setLoading(true);
            await dispatch(AuthThunks.signup(data, invitationToken));
        } catch (error: any) {
            setMessage({ message: error.message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <FormProvider {...form}>
            <Loading loading={loading} />
            <Logo />
            <Typography variant="h1" sx={{ mt: 3, fontWeight: 600, color: Colors.tangaroaBlue, textAlign: 'center' }}>
                {invitationToken ? `Hello, You've been invited to a at Pakado!` : 'Create an account'}
            </Typography>
            <Typography variant="h6" sx={{ mt: 2, fontWeight: 'normal', color: Colors.lynchBlue }}>
                {invitationToken ? 'Please register your account to Continue' : 'Start your 30-day free trial.'}
            </Typography>
            <form style={{ width: '100%' }} onSubmit={form.handleSubmit(onSubmit)}>
                <Box sx={{ mt: 3 }}>
                    <Alert isSuccess={isSuccess} message={message} clearMessage={clearMessage} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <InputText name="name" label="Name" placeholder="Enter your name" />
                        <InputText name="email" label="Email" placeholder="Enter your email" />
                        <InputText name="password" label="Password*" type="password" placeholder="Create a password" />
                        <PrimaryButton title="Get started" submit />
                        {!invitationToken && <LoginGoogleButton title="Sign up" />}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                            <Typography variant="subtitle1" sx={{ color: Colors.lynchBlue, fontWeight: 'normal' }}>
                                Already have an account?
                            </Typography>
                            <Link to={Screens.Login}>
                                <Typography variant="subtitle1" sx={{ color: Colors.slateBlueViolet, fontWeight: 500 }}>
                                    Log in
                                </Typography>
                            </Link>
                        </Box>
                    </Box>
                </Box>
            </form>
        </FormProvider>
    );
};
